/**
 * Unified enemy level for battles: player-relative variance by difficulty, deterministic
 * per encounter seed, clamped to the enemy type's level band. Used by ensure-battle-attempt
 * (persisted on `EnemyInstance.level`) and the battle UI (same inputs → same level).
 */

function getDifficultyMaxOffset(difficulty: string): number {
	switch (difficulty.toUpperCase()) {
		case "EASY":
			return 1;
		case "MEDIUM":
			return 2;
		case "HARD":
			return 3;
		default:
			return 2;
	}
}

/** xmur3-style 32-bit hash (stable across JS runtimes for the same input). */
export function hashStringToSeed(input: string): number {
	let h = 1779033703 ^ input.length;
	for (let i = 0; i < input.length; i += 1) {
		h = Math.imul(h ^ input.charCodeAt(i), 3432918353);
		h = (h << 13) | (h >>> 19);
	}
	h = Math.imul(h ^ (h >>> 16), 2246822507);
	h = Math.imul(h ^ (h >>> 13), 3266489909);
	return (h ^ (h >>> 16)) >>> 0;
}

function seededRandom01(seed: number): number {
	let t = seed + 0x6d2b79f5;
	t = Math.imul(t ^ (t >>> 15), t | 1);
	t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
	return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function seededRandomIntInclusive(
	min: number,
	max: number,
	seed: number,
): number {
	const lo = Math.ceil(Math.min(min, max));
	const hi = Math.floor(Math.max(min, max));
	const r = seededRandom01(seed);
	return Math.floor(r * (hi - lo + 1)) + lo;
}

export interface ComputeBattleEnemyLevelParams {
	playerLevel: number;
	difficulty: string;
	/**
	 * Must match `buildEncounterSeedKey(...)` for this attempt (same string server + client).
	 */
	encounterSeedKey: string;
	minimumLevel: number;
	maximumLevel: number;
}

/**
 * Level ≈ player level ± a difficulty-scoped offset (deterministic from seed), then clamped
 * to [minimumLevel, maximumLevel].
 */
export function computeBattleEnemyLevel(
	params: ComputeBattleEnemyLevelParams,
): number {
	const min = Math.max(1, Math.floor(params.minimumLevel));
	const max = Math.max(min, Math.floor(params.maximumLevel));
	const pl = Math.max(1, Math.floor(params.playerLevel));
	const maxOffset = getDifficultyMaxOffset(params.difficulty);
	const seed = hashStringToSeed(
		`enemyLevel:${params.encounterSeedKey}:${params.difficulty.toUpperCase()}:${pl}`,
	);
	const offset = seededRandomIntInclusive(-maxOffset, maxOffset, seed);
	const raw = Math.max(1, pl + offset);
	return Math.min(max, Math.max(min, raw));
}
